import { createSelector } from "@reduxjs/toolkit";
import { sortBy } from "lodash-es";

import { BulkImportJobType, OrgUserType } from "components/server-types";
import { RootState } from "store";

export const createAppSelector = createSelector.withTypes<RootState>();

// This file only has the most basic selectors that do not have parameters, and do not filter
// or join objects.

// Single attribute selectors //

export const selectCurrentUserId = (state: RootState) => state.auth.user_id;
export const selectUserJWT = (state: RootState) => state.auth.collaboration_jwt;

// This needs to be in base.ts instead of view.ts to avoid circular dependencies
export const selectCurrentCourseId = (state: RootState) => state.view.currentCourseId;

export const selectClassUsers = createAppSelector([(state) => state.class_users], (class_users) =>
  Object.values(class_users),
);

// Pubsub Slice Selectors //
export const selectCourses = createAppSelector([(state) => state.courses], (courses) =>
  Object.values(courses),
);

/** Caution: This selector returns *all* course users, including disabled ones.
 * If you need only course users that are not disabled, use `selectEnabledCourseUsers` instead.
 */
export const selectCourseUsers = createAppSelector(
  [(state) => state.course_users],
  (course_users) => Object.values(course_users),
);

export const selectClassSessions = createAppSelector(
  [(state) => state.class_sessions],
  (class_sessions) => Object.values(class_sessions),
);

export const selectUsers = createAppSelector([(state) => state.users], (users) =>
  Object.values(users),
);

export const selectTopics = createAppSelector([(state) => state.topics], (topics) =>
  Object.values(topics),
);

export const selectActivities = createAppSelector([(state) => state.activities], (activities) =>
  Object.values(activities),
);

export const selectPages = createAppSelector([(state) => state.pages], (pages) =>
  Object.values(pages),
);

export const selectTerms = createAppSelector([(state) => state.terms], (terms) =>
  Object.values(terms),
);

export const selectCourseCodes = createAppSelector(
  [(state) => state.course_codes],
  (course_codes) => Object.values(course_codes),
);

export const selectHasCourseCodes = createAppSelector(
  [selectCourseCodes],
  (course_codes) => course_codes.length > 0,
);

export const selectCourseCodeGroupings = createAppSelector(
  [(state) => state.course_code_groupings],
  (course_code_groupings) => Object.values(course_code_groupings),
);

// This returns all orgs, including disabled ones.
export const selectOrgs = createAppSelector([(state) => state.orgs], (orgs) =>
  sortBy(Object.values(orgs), (org) => org.name.toLowerCase()),
);

export const selectOrgUsers = createAppSelector(
  [(state) => state.org_users],
  (org_users): OrgUserType[] => Object.values(org_users),
);

export const selectOutcomes = createAppSelector([(state) => state.outcomes], (outcomes) =>
  sortBy(Object.values(outcomes), (o) => o.title.toLowerCase()),
);

export const selectOutcomeSubgroups = createAppSelector(
  [(state) => state.outcome_subgroups],
  (outcome_subgroups) => sortBy(Object.values(outcome_subgroups), (os) => os.title.toLowerCase()),
);

export const selectOutcomeGroups = createAppSelector(
  [(state) => state.outcome_groups],
  (outcome_groups) => sortBy(Object.values(outcome_groups), (og) => og.title.toLowerCase()),
);

export const selectOutcomeById = createAppSelector(
  [(state) => state.outcomes, (state, outcomeId: string) => outcomeId],
  (outcomes, outcomeId) => outcomes[outcomeId],
);

export const selectCourseOutcomes = createAppSelector(
  [(state) => state.course_outcomes],
  (course_outcomes) => Object.values(course_outcomes),
);

export const selectPageOutcomes = createAppSelector(
  [(state) => state.page_outcomes],
  (page_outcomes) => Object.values(page_outcomes),
);

export const selectPageGroups = createAppSelector([(state) => state.page_groups], (page_groups) =>
  Object.values(page_groups),
);

export const selectPageGroupUsers = createAppSelector(
  [(state) => state.page_group_users],
  (page_group_users) => Object.values(page_group_users),
);

export const selectOutcomeAssessments = createAppSelector(
  [(state) => state.outcome_assessments],
  (outcome_assessments) => Object.values(outcome_assessments),
);

export const selectAssessments = createAppSelector([(state) => state.assessments], (assessments) =>
  Object.values(assessments),
);

export const selectSubmissions = createAppSelector([(state) => state.submissions], (submissions) =>
  Object.values(submissions),
);

export const selectChatMessages = createAppSelector(
  [(state) => state.chat_messages],
  (chat_messages) => Object.values(chat_messages),
);

export const selectBulkImportJobById = createAppSelector(
  [(state) => state.bulk_import_jobs, (state, jobId: string) => jobId],
  (bulk_import_jobs, jobId) => bulk_import_jobs[jobId] as BulkImportJobType | undefined,
);

export const selectBreakoutGroups = createAppSelector(
  [(state) => state.breakout_groups],
  (breakout_groups) => Object.values(breakout_groups),
);
